import React, {useState} from 'react';
import Page from "../../../components/Page";
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import 'mapbox-gl/dist/mapbox-gl.css';
import "../../../services/mabox-gl";
import VisitorsDensityMap from "../../../components/Maps/VisitorsDensityMap";
import Card from "../../../components/Card";
import DateInput from "../../../components/InsightControls/DateInput";
import {DateTime} from "luxon";
import SiteSelect from "../../../components/InsightControls/SiteSelect";
import {useOperator} from "../../../context/OperatorContext";
import {Site} from "../../../types/Site";
import Selector from "../../../components/Form/Selector";
import {Item} from "../../../components/Form/Selector/Selector";
import VisitorsDensityTimelineMap from "../../../components/Maps/VisitorsDensityPlaybackMap";

// Helpers ----

const breadcrumbs = {
  "Insights": '/',
  "Visitors": '/insights/visitors',
  'Visitor Density': '/insights/visitors/density',
}

// Map modes
export const MAP_MODES = {
  SUMMARY: 'summary',
  TIMELINE: 'timeline',
} as const

const mapModeItems: Item<MapMode>[] = [
  {id: MAP_MODES.SUMMARY, name: "Summary"},
  {id: MAP_MODES.TIMELINE, name: "Playback"},
]

// Intervals
export const INTERVALS = {
  '30_MINS': '30-mins',
  '1_HOUR': '1-hour',
  '2_HOURS': '2-hours',
  '3_HOURS': '3-hours',
  '4_HOURS': '4-hours',
  '6_HOURS': '6-hours',
  '12_HOURS': '12-hours',
  '24_HOURS': '24-hours',
} as const

const intervalDurationItems: Item<Interval>[] = [
  {id: INTERVALS['30_MINS'], name: "30 mins"},
  {id: INTERVALS['1_HOUR'], name: "1 hour"},
  {id: INTERVALS['2_HOURS'], name: "2 hours"},
  {id: INTERVALS['3_HOURS'], name: "3 hours"},
  {id: INTERVALS['4_HOURS'], name: "4 hours"},
  {id: INTERVALS['6_HOURS'], name: "6 hours"},
  {id: INTERVALS['12_HOURS'], name: "12 hours"},
  {id: INTERVALS['24_HOURS'], name: "24 hours"},
]

// Default filters
const defaultFilters: Filters = {
  mode: MAP_MODES.SUMMARY,
  since: DateTime.now().minus({days: 14}).startOf('day'),
  until: DateTime.now().endOf('day'),
  siteId: null,
  interval: INTERVALS['30_MINS'],
}

// Types ----

export type MapMode = typeof MAP_MODES[keyof typeof MAP_MODES];
export type Interval = typeof INTERVALS[keyof typeof INTERVALS];

type Filters = {
  mode: MapMode
  since: DateTime,
  until: DateTime,
  siteId: number | null,
  interval: Interval,
}

// Page ----

export function VisitorDensity() {
  const {termFor, isMultiSite} = useOperator();
  const [filters, setFilters] = useState<Filters>({...defaultFilters});

  // Helpers
  function selectDateRangeSince(since: DateTime) {
    setFilters(currentFilters => {
      return {
        ...currentFilters,
        since: since.startOf('day')};
    })
  }

  function selectDateRangeUntil(until: DateTime) {
    setFilters(currentFilters => {
      return {
        ...currentFilters,
        until: until.endOf('day'),
      };
    })
  }

  function selectSite(site: Site | null) {
    setFilters(currentFilters => {
      return {...currentFilters, siteId: site?.id ?? null};
    })
  }

  function selectMapMode(mode: Item<MapMode>) {
    setFilters(currentFilters => {
      return {...currentFilters, mode: mode.id};
    })
  }

  function selectInterval(interval: Item<Interval>) {
    setFilters(currentFilters => {
      return {...currentFilters, interval: interval.id};
    })
  }

  return (
    <Page title='Visitor Density' breadcrumbs={breadcrumbs}>
      <div className='flex flex-col gap-4'>
        <Card>
          <div className='flex flex-wrap gap-4'>
            <div className="max-w-[14ch]">
              <Selector
                label='Mode'
                items={mapModeItems}
                selectedId={filters.mode}
                onChange={selectMapMode}
              />
            </div>

            {/* Date Range Since */}
            <DateInput
              id="date-range-since"
              name="date-range-since"
              label="Since"
              value={filters.since}
              onChange={selectDateRangeSince}
            />

            {/* Date Range Until */}
            <DateInput
              id="date-range-until"
              name="date-range-until"
              label="Until"
              value={filters.until}
              onChange={selectDateRangeUntil}
              minDate={filters.since}
              maxDate={filters.since.plus({month: 1})}
            />

            {(filters.mode === MAP_MODES.TIMELINE) &&
              <div className="max-w-[14ch]">
                <Selector
                  label='Interval'
                  items={intervalDurationItems}
                  selectedId={filters.interval}
                  onChange={selectInterval}
                />
              </div>
            }

            {isMultiSite && <SiteSelect id="select-site" name="site" label={`${termFor('Site')}`} onChange={selectSite} showAnySiteOption={false} />}
          </div>
        </Card>

        <div>
          {filters.mode === MAP_MODES.SUMMARY &&
            <VisitorsDensityMap height="40rem" since={filters.since} until={filters.until} siteId={filters.siteId} />
          }

          {filters.mode === MAP_MODES.TIMELINE &&
            <VisitorsDensityTimelineMap height="40rem" since={filters.since} until={filters.until} siteId={filters.siteId} interval={filters.interval}/>
          }
        </div>
      </div>
    </Page>
  );
}